import React, { useEffect } from 'react';
import { Card, CardContent } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Loader2 } from 'lucide-react';
import { useEvent, useLazyQuery } from '@soap/modules';

interface AssessmentPreviewSectionProps {
    assessmentId: string;
    unitId?: string;
    assessmentPreviews: Map<string, any>;
    setAssessmentPreviews: React.Dispatch<React.SetStateAction<Map<string, any>>>;
    isDeleting?: boolean;
    /** When > 0 after E149, do not render stale useLazyQuery result — wait for E154 into the map. */
    previewEpoch?: number;
}

export function AssessmentPreviewSection({ 
    assessmentId,
    unitId,
    assessmentPreviews, 
    setAssessmentPreviews,
    isDeleting = false,
    previewEpoch = 0
}: AssessmentPreviewSectionProps) {
    const [getAssessment, gotAssessment] = useLazyQuery("C188v1_GetAssessment");

    // Authoritative preview payload — avoids showing stale useLazyQuery data after cache invalidation
    useEvent({
        eventName: 'E154v1_GotAssessmentQuestions',
        onEventReceived: (event: any) => {
            if (!event) return;
            setAssessmentPreviews((prev) => {
                const newMap = new Map(prev);
                newMap.set(assessmentId, event);
                return newMap;
            });
        }
    });

    useEffect(() => {
        if (assessmentId && !assessmentPreviews.has(assessmentId) && !isDeleting) {
            getAssessment({
                C188_AssessmentId: assessmentId
            });
        }
    }, [assessmentId, isDeleting, assessmentPreviews, getAssessment]);

    const fromMap = assessmentPreviews.get(assessmentId);
    const assessment =
        fromMap ?? (previewEpoch === 0 ? gotAssessment : undefined);

    if (!assessment) {
        return (
            <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                <span className="ml-2 text-sm text-gray-500">Loading assessment preview...</span>
            </div>
        );
    }

    return (
        <div className="space-y-6">
                {assessment.e154_Instructions && (
                    <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Instructions</h4>
                        <div className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: assessment.e154_Instructions }} />
                    </div>
                )}

                {assessment.e154_Sections?.map((section: any, sectionIndex: number) => (
                    <div key={sectionIndex} className="space-y-4">
                        <h4 className="text-lg font-semibold text-gray-900 border-b pb-2">
                            Section {sectionIndex + 1}: {section.e154_SectionTitle || `Section ${sectionIndex + 1}`}
                        </h4>
                        {section.e154_Questions?.map((question: any, questionIndex: number) => (
                            <Card key={questionIndex} className="border-l-4 border-l-action-blue">
                                <CardContent className="p-4">
                                    <div className="space-y-3">
                                        <div className="flex items-start justify-between">
                                            <h5 className="font-medium text-gray-900">
                                                Question {questionIndex + 1}: {question.e154_QuestionText}
                                            </h5>
                                            <Badge variant="outline" className="ml-2">
                                                {question.e154_QuestionTypeKey} ({question.e154_QuestionPoints || 0} pts)
                                            </Badge>
                                        </div>
                                        
                                        {question.e154_QuestionData && (
                                            <div className="bg-gray-50 p-3 rounded text-sm">
                                                <p className="font-medium text-gray-700 mb-2">Answer Options:</p>
                                                {(() => {
                                                    const possibleAnswers = question.e154_QuestionData.e154_PossibleAnswers;
                                                    if (!possibleAnswers || possibleAnswers.length === 0) {
                                                        return <p className="text-gray-500 text-xs italic">No answer options available</p>;
                                                    }
                                                    return (
                                                        <ul className="list-disc list-inside space-y-1 text-gray-600">
                                                            {possibleAnswers.map((answer: any, i: number) => {
                                                                const enumeration = answer.e154_Enumeration;
                                                                const key = enumeration?.key;
                                                                const value = enumeration?.value;
                                                                const correctAnswerKeys = question.e154_QuestionData.e154_CorrectAnswerKeys;
                                                                const correctAnswerKey = question.e154_QuestionData.e154_CorrectAnswerKey;
                                                                const isCorrect = correctAnswerKeys?.includes(key) || correctAnswerKey === key;
                                                                
                                                                return (
                                                                    <li key={i}>
                                                                        {value || 'No value'}
                                                                        {isCorrect && (
                                                                            <span className="ml-2 text-confirm-action font-semibold">✓ Correct</span>
                                                                        )}
                                                                    </li>
                                                                );
                                                            })}
                                                        </ul>
                                                    );
                                                })()}
                                            </div>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                ))}
        </div>
    );
}

